// Order Variable nodes by the workflow's execution order.
//
// The Variables side panel originally listed variables in array order, which
// reflects creation time and feels random once the user reorganizes the
// canvas. This util walks the graph from the Start node in BFS order and
// records the first time each variable name is touched (either set by a
// "Set Variable" node or read via an @VarName reference in any string param).
// Variables that are never referenced fall to the bottom in their original
// order, so newly-added variables remain visible while still being sorted
// reasonably as soon as they're wired up.

var AT_REF_RE = /@([A-Za-z_][\w]*)/g;

// Pull every @VarName reference out of a single param value (which may be a
// string, a number, a boolean, or a nested object/array). Non-string leaves
// are stringified — that mirrors how the runtime resolves @refs anywhere a
// template might appear.
function collectAtRefs(value, out) {
  if (value === null || value === undefined) return;
  if (typeof value === 'string') {
    var m;
    AT_REF_RE.lastIndex = 0;
    while ((m = AT_REF_RE.exec(value)) !== null) {
      out[m[1]] = true;
    }
    return;
  }
  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      for (var i = 0; i < value.length; i++) collectAtRefs(value[i], out);
      return;
    }
    for (var k in value) {
      if (Object.prototype.hasOwnProperty.call(value, k)) {
        collectAtRefs(value[k], out);
      }
    }
  }
}

// Names of variables a single node touches: explicit target on Set Variable,
// plus any @VarName referenced anywhere in its paramValues.
function variableNamesTouchedByNode(node) {
  var touched = {};
  if (!node) return touched;
  var pv = node.paramValues || {};
  if (node.icon === 'set-variable') {
    var target = String(pv.variable || pv.var || pv.varName || '').trim();
    if (target) touched[target] = true;
  }
  collectAtRefs(pv, touched);
  return touched;
}

// Find the Start node — fall back to any root (no incoming connection) if
// there's no explicit start, so the panel still sorts sensibly on partial
// graphs.
function findStartNodeId(nodes, connections) {
  for (var i = 0; i < nodes.length; i++) {
    var n = nodes[i];
    if (n && (n.icon === 'start' || n.kind === 'start' || n.nodeType === 'start')) {
      return n.id;
    }
  }
  var hasIncoming = {};
  for (var c = 0; c < connections.length; c++) {
    if (connections[c] && connections[c].to) hasIncoming[connections[c].to] = true;
  }
  for (var j = 0; j < nodes.length; j++) {
    var nj = nodes[j];
    if (!nj || nj.icon === 'variables') continue;
    if (!hasIncoming[nj.id]) return nj.id;
  }
  return null;
}

// Sort variable nodes by the order they're first touched by a BFS walk from
// Start. Variables never referenced keep their original relative order at
// the end of the list.
function sortVariableNodesByExecutionOrder(variableNodes, allNodes, connections) {
  variableNodes = variableNodes || [];
  if (variableNodes.length <= 1) return variableNodes.slice();
  allNodes = allNodes || [];
  connections = connections || [];

  var nodeById = {};
  for (var n = 0; n < allNodes.length; n++) {
    if (allNodes[n] && allNodes[n].id) nodeById[allNodes[n].id] = allNodes[n];
  }

  var childrenMap = {};
  for (var ci = 0; ci < connections.length; ci++) {
    var conn = connections[ci];
    if (!conn || !conn.from || !conn.to) continue;
    if (!childrenMap[conn.from]) childrenMap[conn.from] = [];
    if (childrenMap[conn.from].indexOf(conn.to) === -1) childrenMap[conn.from].push(conn.to);
  }

  var startId = findStartNodeId(allNodes, connections);
  var firstSeen = {}; // varName -> visit step
  var step = 0;

  if (startId) {
    var visited = {};
    var queue = [startId];
    while (queue.length) {
      var id = queue.shift();
      if (visited[id]) continue;
      visited[id] = true;
      var node = nodeById[id];
      if (node && node.icon !== 'variables') {
        var touched = variableNamesTouchedByNode(node);
        for (var name in touched) {
          if (Object.prototype.hasOwnProperty.call(touched, name) && firstSeen[name] === undefined) {
            firstSeen[name] = step;
          }
        }
        step++;
      }
      var kids = childrenMap[id] || [];
      for (var ki = 0; ki < kids.length; ki++) {
        if (!visited[kids[ki]]) queue.push(kids[ki]);
      }
    }
  }

  var sorted = variableNodes.slice();
  var originalIndex = {};
  for (var oi = 0; oi < sorted.length; oi++) originalIndex[sorted[oi].id] = oi;

  sorted.sort(function(a, b) {
    var an = String(a.title || '').trim();
    var bn = String(b.title || '').trim();
    var ai = firstSeen[an];
    var bi = firstSeen[bn];
    var aHas = ai !== undefined;
    var bHas = bi !== undefined;
    if (aHas && bHas) {
      if (ai !== bi) return ai - bi;
      return originalIndex[a.id] - originalIndex[b.id];
    }
    if (aHas) return -1;
    if (bHas) return 1;
    return originalIndex[a.id] - originalIndex[b.id];
  });

  return sorted;
}

module.exports = {
  sortVariableNodesByExecutionOrder: sortVariableNodesByExecutionOrder
};
